import {
  Language,
  DocumentPackageType,
  BuildingPackageType,
  LayoutType,
  ProcessorPreferenceKey,
  EmployerRole,
  RfpRegion,
  SiteDueDiligenceFacilityType,
  ServiceEntityType,
  OFFER_PROCESSOR_CATEGORIES,
} from '@consigli/types';
import { useState } from 'react';

// import { TemplateMode } from '../site-due-diligence-fields';
import { ServiceFormData } from '../types';
import { CommentsMode, PackageInputMode } from '../version-compare-section';

export interface BidReviewBidEntry {
  id: string;
  providerName: string;
  language: string;
  files: File[];
}

export const createEmptyBid = (): BidReviewBidEntry => ({
  id: crypto.randomUUID(),
  providerName: '',
  language: '',
  files: [],
});

const allOfferProcessors = (): ProcessorPreferenceKey[] => [
  ...OFFER_PROCESSOR_CATEGORIES.RISK.map((option) => option.key),
  ...OFFER_PROCESSOR_CATEGORIES.INSIGHT.map((option) => option.key),
  ...OFFER_PROCESSOR_CATEGORIES.GAPS.map((option) => option.key),
];

export interface ServiceFormState {
  serviceType: ServiceEntityType | '';
  name: string;
  language: Language;
  files: File[];

  // Document Package specific
  documentPackageType: DocumentPackageType;
  selectedConflictTypes: ProcessorPreferenceKey[];
  employerRole: EmployerRole | null;

  // Building Package specific
  buildingPackageType: BuildingPackageType;
  inputType: 'files' | 'json';

  // Layout Optimization specific
  layoutOptimizationType: LayoutType;
  comments: string;
  floorHeight: number | null;

  // RFP/RFQ Review specific
  rfpRegion: RfpRegion | null;

  // Site Due Diligence specific
  siteDueDiligenceFacilityType: SiteDueDiligenceFacilityType | null;
  // templateMode: TemplateMode;
  // customTemplateFile: File | null;
  // selectedCustomer: string | null;
  locationLat: string;
  locationLon: string;
  documentFiles: File[];
  // gisFiles: File[];
  // urls: string[];
  // allowLLMForDocuments: boolean;

  // Bid Review (OFFER) specific
  bidReviewBids: BidReviewBidEntry[];
  bidReviewServices: string[];
  bidReviewAutoFillLevelingSheet: boolean;
  bidReviewLevelingSheetFiles: File[];
  tenderPackageMode: 'existing' | 'new';
  existingTenderPackageId: string | null;
  tenderPackageName: string;
  tenderFiles: File[];

  // Version Compare specific
  versionComparePrimaryMode: PackageInputMode;
  versionCompareSecondaryMode: PackageInputMode;
  versionComparePrimaryPackageId: string | null;
  versionCompareSecondaryPackageId: string | null;
  versionComparePrimaryFiles: File[];
  versionCompareSecondaryFiles: File[];
  versionCompareCommentsMode: CommentsMode;
  versionCompareCommentRegisterFiles: File[];
}

/**
 * This hook is used to manage the state of the entire service form.
 * This includes all the service types.
 * It also manages the errors of the service form.
 * @returns The state of the service form.
 */
export const useUnifiedServiceForm = () => {
  const [state, setState] = useState<ServiceFormState>({
    serviceType: ServiceEntityType.DOCUMENT_PACKAGE,
    name: '',
    language: Language.EN,
    files: [],
    documentPackageType: DocumentPackageType.TENDER_DOCUMENTS,
    selectedConflictTypes: [],
    employerRole: null,
    rfpRegion: null,
    buildingPackageType: BuildingPackageType.CEILING_GRID,
    inputType: 'files',
    layoutOptimizationType: LayoutType.CEILING_PLAN,
    comments: '',
    floorHeight: null,
    // Site Due Diligence defaults
    siteDueDiligenceFacilityType: null,
    // templateMode: 'default',
    // customTemplateFile: null,
    // selectedCustomer: null,
    locationLat: '',
    locationLon: '',
    documentFiles: [],
    // gisFiles: [],
    // urls: [],
    // allowLLMForDocuments: false,

    // Bid Review (OFFER) defaults
    bidReviewBids: [createEmptyBid()],
    bidReviewServices: [],
    bidReviewAutoFillLevelingSheet: false,
    bidReviewLevelingSheetFiles: [],
    tenderPackageMode: 'existing',
    existingTenderPackageId: null,
    tenderPackageName: '',
    tenderFiles: [],

    // Version Compare defaults
    versionComparePrimaryMode: PackageInputMode.SELECT_EXISTING,
    versionCompareSecondaryMode: PackageInputMode.SELECT_EXISTING,
    versionComparePrimaryPackageId: null,
    versionCompareSecondaryPackageId: null,
    versionComparePrimaryFiles: [],
    versionCompareSecondaryFiles: [],
    versionCompareCommentsMode: CommentsMode.NO_COMMENTS,
    versionCompareCommentRegisterFiles: [],
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ServiceFormData, string>>>({});

  const updateField = <K extends keyof ServiceFormState>(field: K, value: ServiceFormState[K]) => {
    setState((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field as keyof ServiceFormData]) {
      setErrors((prev) => ({ ...prev, [field as keyof ServiceFormData]: undefined }));
    }

    // Special case: when serviceType changes, clear all errors since validation rules change completely
    if (field === 'serviceType') {
      setErrors({});
    }

    // Special case: when inputType changes, also clear files error since validation rules change
    if (field === 'inputType') {
      setErrors((prev) => ({ ...prev, files: undefined }));
    }

    // Special case: when documentPackageType changes, reset selectedConflictTypes
    // and reset site-due-diligence fields when switching away from it
    if (field === 'documentPackageType') {
      setState((prev) => ({
        ...prev,
        // Bid Review pre-selects every processor; other package types start empty.
        selectedConflictTypes: value === DocumentPackageType.OFFER ? allOfferProcessors() : [],
        // Reset RFP region when switching away from RFP/RFQ Review
        ...(value !== DocumentPackageType.RFP_RFQ_REVIEW && {
          rfpRegion: null,
        }),
        // Reset site-due-diligence fields when switching to a different type
        ...(value !== DocumentPackageType.SITE_DUE_DILIGENCE && {
          siteDueDiligenceFacilityType: null,
          // templateMode: 'default' as TemplateMode,
          // customTemplateFile: null,
          // selectedCustomer: null,
          locationLat: '',
          locationLon: '',
          documentFiles: [],
          // gisFiles: [],
          // urls: [],
          // allowLLMForDocuments: false,
        }),
        // Reset bid review fields when switching to a different type
        ...(value !== DocumentPackageType.OFFER && {
          bidReviewBids: [createEmptyBid()],
          bidReviewServices: [],
          bidReviewAutoFillLevelingSheet: false,
          bidReviewLevelingSheetFiles: [],
          tenderPackageMode: 'existing' as const,
          existingTenderPackageId: null,
          tenderPackageName: '',
          tenderFiles: [],
        }),
        // Reset version compare fields when switching to a different type
        ...(value !== DocumentPackageType.VERSION_COMPARE && {
          versionComparePrimaryMode: PackageInputMode.SELECT_EXISTING,
          versionCompareSecondaryMode: PackageInputMode.SELECT_EXISTING,
          versionComparePrimaryPackageId: null,
          versionCompareSecondaryPackageId: null,
          versionComparePrimaryFiles: [],
          versionCompareSecondaryFiles: [],
          versionCompareCommentsMode: CommentsMode.NO_COMMENTS,
          versionCompareCommentRegisterFiles: [],
        }),
      }));
    }
  };

  const resetForm = () => {
    setState((prev) => ({
      ...prev,
      name: '',
      files: [],
      selectedConflictTypes:
        prev.documentPackageType === DocumentPackageType.OFFER ? allOfferProcessors() : [],
      employerRole: null,
      rfpRegion: null,
      comments: '',
      floorHeight: null,
      // Site Due Diligence reset
      siteDueDiligenceFacilityType: null,
      // templateMode: 'default',
      // customTemplateFile: null,
      // selectedCustomer: null,
      locationLat: '',
      locationLon: '',
      documentFiles: [],
      // gisFiles: [],
      // urls: [],
      // allowLLMForDocuments: false,

      // Bid Review (OFFER) reset
      bidReviewBids: [createEmptyBid()],
      bidReviewServices: [],
      bidReviewAutoFillLevelingSheet: false,
      bidReviewLevelingSheetFiles: [],
      tenderPackageMode: 'existing' as const,
      existingTenderPackageId: null,
      tenderPackageName: '',
      tenderFiles: [],

      // Version Compare reset
      versionComparePrimaryMode: PackageInputMode.SELECT_EXISTING,
      versionCompareSecondaryMode: PackageInputMode.SELECT_EXISTING,
      versionComparePrimaryPackageId: null,
      versionCompareSecondaryPackageId: null,
      versionComparePrimaryFiles: [],
      versionCompareSecondaryFiles: [],
      versionCompareCommentsMode: CommentsMode.NO_COMMENTS,
      versionCompareCommentRegisterFiles: [],
    }));
    setErrors({});
  };

  const setErrorsFromValidation = (
    validationErrors: Partial<Record<keyof ServiceFormData, string>>,
  ) => {
    setErrors(validationErrors);
  };

  const clearErrors = () => {
    setErrors({});
  };

  return {
    // State
    ...state,
    errors,

    // Actions
    updateField,
    resetForm,
    setErrorsFromValidation,
    clearErrors,
  };
};
